import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import ColumnPatient from "../components/ColumnPatient";
import { deleteCookie } from "../lib/utils/cookies";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { getCheckInDoctor, getDoctorAvailability } from "../lib/apis/doctor";
import { hitRefreshToken } from "../lib/apis/user";
import {
  checkAvailability,
  getBookingListByAvailabilityId,
} from "../lib/apis/booking";
import {
  Booking,
  DocAvailability,
  Doctor,
  SessionAdjustment,
} from "../lib/utils/types";
import { useInterval } from "../lib/utils/useInterval";
import { sortQueueBookings, isTransferredPatient } from "../lib/utils/queueSort";
import { formatDoctorName } from "../lib/utils/formatDoctorName";
import ReactDropdown, { Option } from "react-dropdown";
import ProfilePicture from "./ProfilePicture";
import { getAudioLanguageFromLocalStorage } from "../lib/utils/speechUtils";
import useSpeakOngoingQueue, { OngoingAnnouncement } from "../lib/customHooks/useSpeakOngoingQueue";
import AudioPermissionModal from "./AudioPermissionModal";
import { useUserData } from "../lib/contexts/UserContext";
import { getRoomMapping } from "../lib/apis/room";
import { useTVConfiguration } from "../lib/contexts/TVConfigurationContext";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import { IoWarning } from "react-icons/io5";

const CompactRoomDisplay = ({ roomName }: { roomName: string }) => {
  if (!roomName) return null;

  return (
    <div className="mb-2">
      <div className="bg-gradient-to-r from-blue-500 to-blue-800 rounded-full pz-3 py-1 shadow-md border border-blue-600/20">
        <div className="flex items-center justify-center">
          <div className="bg-white/20 rounded-full p-1 w-8 h-8 mr-2 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-white text-[10px] font-medium opacity-80">
              Room
            </p>
            <p className="text-white text-sm font-semibold truncate">
              {roomName}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LiveQueue = ({ mapping_id, onOngoingChange }: { mapping_id: string; onOngoingChange?: (mappingId: string) => void }) => {
  const { hospData, allDoctorsData } = useHospDocData();
  const { accessToken, setAccessToken } = useUserData();
  const navigate = useNavigate();

  // Auto-scroll state for TV display
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const [sessionDetails, setSessionDetails] = useState<{
    id: string;
    queue_type: string;
  }>();

  const { tvConfig } = useTVConfiguration();
  const [docDetails, setDocDetails] = useState<Doctor>();
  const [docAvail, setDocAvail] = useState<DocAvailability[]>();
  const [SelectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [index, setIndex] = useState<number | undefined>(moment().day() + 1);
  const [inClinicData, setInClinicData] = useState<Booking[]>();
  const [options, setOptions] = useState<Option[]>();
  const [session, setSession] = useState<Option>();
  const [, setOnGoing] = useState<Booking>();
  const [lastAnnouncedBookingId, setLastAnnouncedBookingId] = useState<string | undefined>(undefined);
  const [roomNames, setRoomNames] = useState<
    Array<{ room_id: string; room_name: string }>
  >([]);

  const isNameHidden = tvConfig?.patient_name === false;
  const { announce_first_name_tv } = useFeatureFlags();

  const ongoingAnnouncements: OngoingAnnouncement[] = (inClinicData ?? [])
    .filter(
      (item) =>
        item.status === 2 ||
        (item.transferred_doctor_mapping_id === mapping_id &&
          item.transfer_status === 2)
    )
    .map((item) => {
      let name: string;
      if (isNameHidden) {
        name = item.token_number ? `Token ${item.token_number}` : "";
      } else {
        const raw = item.booking_alias || item.full_name || "";
        name = announce_first_name_tv ? (raw.trim().split(/\s+/)[0] ?? "") : raw;
      }
      return {
        bookingId: item.booking_id,
        patientName: name,
        transferCycleId: item.recent_transfer_id ?? undefined,
        room:
          roomNames.length === 1
            ? roomNames[0].room_name
            : item.room_name || undefined,
      };
    });

  const {
    showPermissionModal,
    handleAllowAudio,
    handleCancelAudio,
  } = useSpeakOngoingQueue(
    getAudioLanguageFromLocalStorage(),
    docDetails?.full_name,
    ongoingAnnouncements
  );

  const fetchQueueData = async (availability_id: string) => {
    const inclinic_data = await getBookingListByAvailabilityId(
      availability_id,
      SelectedDate
    );
    console.log("fetch");
    if (inclinic_data?.status === 200) {
      // console.log("bookings",inclinic_data.data.result);
      const data: Booking[] = inclinic_data.data.result;

      const enrichedData = data.map((item) => {
        let type = item.booking_mode;
        if (item.comments?.toLowerCase() === "follow-up") {
          type = "followup";
        }
        return {
          ...item,
          booking_mode: type,
        };
      });

      const filtered = enrichedData.filter((item) => {
        // Membership: belongs to this doctor's queue
        if (item.transferred_doctor_mapping_id !== mapping_id) return false;

        // Statuses we render: waiting, ongoing, transferred-in waiting/ongoing, or in history/scan
        const statusOk =
          item.status === 1 ||
          item.status === 2 ||
          (item.transfer_status !== undefined && [1, 2, 6, 7, 8, 9].includes(item.transfer_status));
        if (!statusOk) return false;

        // Own patients are scoped to the currently selected session.
        // Transferred-in rows skip the availability check (their availability_id belongs to another doctor).
        const isOwnPatient = item.doctor_mapping_id === item.transferred_doctor_mapping_id;
        if (isOwnPatient && item.availability_id !== availability_id) return false;

        return true;
      });

      // Dedup by booking_id only — same patient can have multiple distinct bookings.
      const isOngoingRow = (i: Booking) => i.status === 2 || i.transfer_status === 2;
      const dedupedByBooking = Array.from(
        filtered
          .reduce((acc, item) => {
            const existing = acc.get(item.booking_id);
            if (!existing || (isOngoingRow(item) && !isOngoingRow(existing))) {
              acc.set(item.booking_id, item);
            }
            return acc;
          }, new Map<string, Booking>())
          .values()
      );

      setInClinicData(dedupedByBooking);
      const ongoingPatient = enrichedData.find((item) => {
        if (item.transferred_doctor_mapping_id !== mapping_id) return false;
        const isOwnOngoing =
          item.status === 2 && item.availability_id === availability_id;
        const isTransferredOngoing = item.transfer_status === 2;
        return isOwnOngoing || isTransferredOngoing;
      });
      if (ongoingPatient !== undefined) {
        setOnGoing(ongoingPatient);
        // Notify parent when a new ongoing patient appears (debounced by booking_id)
        if (ongoingPatient.booking_id !== lastAnnouncedBookingId) {
          setLastAnnouncedBookingId(ongoingPatient.booking_id);
          if (typeof onOngoingChange === "function") onOngoingChange(mapping_id);
        }

        //BROWSER SPEECH SYNTHESIS
        // if (onGoing?.token_number !== ongoingPatient.token_number) {
        //   console.log("Triggering Speech for:", ongoingPatient.full_name);
        //   const text = `Next patient for ${docDetails?.full_name} is ${ongoingPatient.full_name}`;
        //   speakText(text)
        // }
      }
    } else if (inclinic_data?.status === 403 && accessToken) {
      const refresh_data = await hitRefreshToken();
      if (refresh_data?.status === 200) {
        console.log("Refresh");
        setAccessToken(refresh_data.data.result.access_token);

        const api_data = await getBookingListByAvailabilityId(
          session?.value,
          SelectedDate
        );
        if (api_data?.status === 200) {
          const data: Booking[] = api_data.data.result;
          setInClinicData(
            data.filter((item: Booking) => {
              // Membership: belongs to this doctor's queue
              if (item.transferred_doctor_mapping_id !== mapping_id) return false;

              // Statuses we render: waiting, ongoing, transferred-in waiting/ongoing, or in history/scan
              const statusOk =
                item.status === 1 ||
                item.status === 2 ||
                (item.transfer_status !== undefined && [1, 2, 6, 7, 8, 9].includes(item.transfer_status));
              if (!statusOk) return false;

              // Own patients are scoped to the currently selected session.
              // Transferred-in rows skip the availability check.
              const isOwnPatient = item.doctor_mapping_id === item.transferred_doctor_mapping_id;
              if (isOwnPatient && item.availability_id !== session?.value) return false;

              return true;
            })
          );
        }
      } else {
        deleteCookie("hospID");
        navigate("/");
      }
    } else setInClinicData(undefined);
  };

  useEffect(() => {
    setSelectedDate(moment().format("YYYY-MM-DD"));
    setIndex(moment().day() + 1);
  }, []);

  useEffect(() => {
    const fetchDocAvailability = async () => {
      const res = await getDoctorAvailability(mapping_id);
      if (res?.status === 200) {
        // console.log(res.data.result);
        setDocAvail(res.data.result.doctor_availability);
        setDocDetails(res.data.result.doctor_details);
      } else if (res?.status === 403 && accessToken) {
        const refresh_data = await hitRefreshToken();
        if (refresh_data?.status === 200) {
          console.log("Refresh");
          setAccessToken(refresh_data.data.result.access_token);
          const res = await getDoctorAvailability(mapping_id);
          if (res?.status === 200) {
            setDocAvail(res.data.result.doctor_availability);
            setDocDetails(res.data.result.doctor_details);
          }
        }
      }
    };
    const fetchRoomMapping = async () => {
      const res = await getRoomMapping(mapping_id);
      if (res?.status === 200) {
        // console.log(res.data.result);
        setRoomNames(res.data.result || []);
      }
    };
    fetchDocAvailability();
    fetchRoomMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping_id, hospData]);

  useEffect(() => {
    if (docAvail !== undefined) {
      const data: Option[] = docAvail
        .filter((i) => i.day_of_week === index)
        .map((item) => {
          const startTimeMoment = moment(item.start_time, "HH:mm:ss");
          const endTimeMoment = moment(item.end_time, "HH:mm:ss");

          return {
            value: String(item.availability_id),
            label: `${startTimeMoment.format(
              "hh:mmA"
            )} - ${endTimeMoment.format("hh:mmA")}`,
            data: {
              wait_time: item.wait_time.minutes,
              queue_type: item.queue_type,
              max_patients: item.max_patients_per_slot,
            },
            start_time: startTimeMoment, // Assigning moment object
            end_time: endTimeMoment, // Assigning moment object
          };
        })
        .sort((a, b) => {
          return a.start_time.diff(b.start_time);
        });

      const fetchExtension = async () => {
        let newOptions = data.slice();

        for (let i = 0; i < data.length; i++) {
          try {
            const res = await checkAvailability(data[i].value, SelectedDate);
            console.log("checkAvailability", res?.data.result);

            if (res?.status === 200 && res.data.result.length > 0) {
              const api_data: SessionAdjustment[] = res.data.result;

              // eslint-disable-next-line no-loop-func
            api_data.forEach((apiResult) => {
                if (apiResult.type === "Unavailability") {
                  newOptions = newOptions.filter(
                    (session) => apiResult.availability_id !== session.value
                  );
                } else if (apiResult.type === "Extension") {
                  newOptions = newOptions.map((session) => {
                    if (apiResult.availability_id === session.value) {
                      return {
                        ...session,
                        label: `${moment(
                          apiResult.session_start_time,
                          "HH:mm:ss"
                        ).format("hh:mmA")} - ${moment(
                          apiResult.extended_end_time,
                          "HH:mm:ss"
                        ).format("hh:mmA")}`,
                        data: {
                          ...session.data,
                        },
                      };
                    }
                    return session;
                  });
                }
              });
            } else if (res?.status === 404) {
              console.log(
                "Session ID " + data[i].value + " not found, no changes made."
              );
            } else {
              throw new Error("Unhandled response status: " + res.status);
            }
          } catch (error) {
            console.error("Error fetching session availability: ", error);
          }
        }

        setOptions(newOptions);

        const now = moment();
        // const now = moment().set({ hour: 19, minute: 0 });
        console.log(newOptions);
        const currSession = newOptions
          .filter((item) => {
            const startTime = moment(
              String(item?.label)?.split(" - ")[0],
              "HH:mmA"
            ).subtract(30, "minutes"); // Subtract 30 minutes from start time
            const endTime = moment(
              String(item?.label)?.split(" - ")[1],
              "HH:mmA"
            ).add(30, "minutes"); // Add 30 minutes to end time

            return now.isBetween(startTime, endTime); // Check if current time is within the modified range
          })
          .map((item) => {
            return {
              value: String(item.value),
              label: `${moment(
                String(item?.label)?.split(" - ")[0],
                "HH:mm:ss"
              ).format("hh:mmA")} - ${moment(
                String(item?.label)?.split(" - ")[1],
                "HH:mm:ss"
              ).format("hh:mmA")}`,
              data: {
                wait_time: item.data?.wait_time,
                queue_type: item.data?.queue_type,
              },
            };
          });

        console.log(currSession);
        if (currSession && currSession.length > 0) {
          const updatedSession = newOptions.find(
            (opt) => opt.value === currSession[0].value
          );
          setSession(updatedSession || newOptions[0]);
          setSessionDetails({
            id: String(updatedSession?.value || newOptions[0].value),
            queue_type: String(
              updatedSession?.data?.queue_type || newOptions[0].data?.queue_type
            ),
          });
          fetchQueueData(updatedSession?.value || newOptions[0].value);
        } else if (newOptions.length > 0) {
          setSession(newOptions[0]);
          setSessionDetails({
            id: String(newOptions[0].value),
            queue_type: String(newOptions[0].data?.queue_type),
          });
          fetchQueueData(newOptions[0].value);
        } else {
          console.log("No available sessions found");
          setOptions([]);

          // Optionally, handle the case when all sessions are unavailable or removed
        }
      };

      fetchExtension();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SelectedDate, docAvail]);

  useInterval(async () => {
    if (session?.value) {
      fetchQueueData(session?.value);
    }
  }, 6000);

  const changeOption = (arg: Option) => {
    setInClinicData(undefined);
    setSession(arg);

    const matched = docAvail?.find(
      (i) => String(i.availability_id) === arg.value
    );
    if (matched) {
      setSessionDetails({
        id: String(matched.availability_id),
        queue_type: matched.queue_type,
      });
      fetchQueueData(String(matched.availability_id));
    }
  };

  const [addtionalCheckIndata, setAdditionalCheckInData] = useState({
    is_paused: false,
    pause_reason: "",
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const getDoctorCheckIn = async () => {
      try {
        const res = await getCheckInDoctor(
          mapping_id,
          SelectedDate,
          session?.value || ""
        );
        if (res?.status === 200) {
          setAdditionalCheckInData({
            is_paused: res.data.result.is_paused,
            pause_reason: res.data.result.pause_reason,
          });
        }
      } catch (error) {
        console.error("Error fetching doctor check-in:", error);
      }
    };

    // Immediately fetch once
    getDoctorCheckIn();

    // Set polling interval (every 3 seconds)
    intervalId = setInterval(() => {
      getDoctorCheckIn();
    }, 6000);

    // Cleanup on unmount or when session changes
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [mapping_id, SelectedDate, session]);

  const displayPatientRoomname = roomNames.length > 1 && tvConfig?.room_number;

  // Auto-scroll: smooth scroll function for the container
  const smoothScrollContainer = (target: number, duration: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const start = container.scrollTop;
    const distance = target - start;
    let startTime: number | null = null;

    const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
      t /= d / 2;
      if (t < 1) return (c / 2) * t * t + b;
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    };

    const step = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const newScrollY = easeInOutQuad(elapsed, start, distance, duration);
      if (container) {
        container.scrollTop = newScrollY;
      }
      if (elapsed < duration) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  };

  // Auto-scroll: cycle up and down.
  // The cycle is driven by the actual animation duration (not a fixed interval)
  // so each leg fully reaches the top/bottom before reversing.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const PAUSE = 2000; // hold at each end before reversing
    let direction: "down" | "up" = "down";
    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const cycle = () => {
      if (cancelled) return;

      const scrollableHeight = container.scrollHeight - container.clientHeight;

      // Nothing to scroll yet (data still loading / list fits) — re-check later.
      if (scrollableHeight <= 0) {
        timeoutId = setTimeout(cycle, 5000);
        return;
      }

      const duration =
        scrollableHeight * 18 > 5000 ? scrollableHeight * 18 : 5000;
      const target = direction === "down" ? scrollableHeight : 0;

      smoothScrollContainer(target, duration);
      direction = direction === "down" ? "up" : "down";

      // Wait for the full animation to finish, then pause, before the next leg.
      timeoutId = setTimeout(cycle, duration + PAUSE);
    };

    timeoutId = setTimeout(cycle, PAUSE);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="flex flex-col items-center px-4 mx-2 pt-3 pb-4 border-[1.5px] border-gray-300 rounded-xl h-full overflow-hidden">
      <div className="w-full flex justify-end mb-2">
        <ReactDropdown
          options={options ? options : []}
          onChange={changeOption}
          value={session}
          placeholder={"Select a session"}
          menuClassName={`rounded-xl !mt-1 !absolute !text-xs xl:!text-sm z-10 md:!w-full !h-46`}
          controlClassName="!px-1.5 !py-0.5 xl:!px-2 xl:!py-1 h-[20px] !rounded-full flex items-center !self-center !border !border-[#0F3CBE] !text-[#0F3CBE] !text-[10px] xl:!text-xs font-medium !cursor-pointer"
          arrowClassName="!hidden"
        />
      </div>
      <div className="relative flex flex-row items-center w-full bg-[#0F3CBE] rounded-full mb-2 p-2 text-left">
        <div className="flex flex-row w-full justify-between">
          {docDetails?.profile_picture.startsWith("http") ? (
            <img
              className="w-[50px] h-[50px] rounded-full items-center !hidden xl:!block shrink-0 !w-[50px] !h-[50px] !inline-block object-cover"
              src={docDetails?.profile_picture}
              alt="doctor"
            ></img>
          ) : (
            <ProfilePicture
              username={docDetails?.full_name || ""}
              gender={docDetails?.gender}
              className="w-[50px] h-[50px] rounded-full items-center !hidden xl:!block shrink-0 !w-[50px] !h-[50px] !inline-block"
            />
          )}

          <div className="ml-3 flex flex-col justify-center gap-1 w-full text-left">
            <p className="text-white font-bold text-sm md:text-base xl:text-lg truncate max-w-[400px] leading tight text-left">
              {formatDoctorName(docDetails?.full_name, allDoctorsData?.find((d) => d.mapping_id === mapping_id)?.show_name_prefix !== false)}
            </p>
            <p className="flex items-start text-xs text-white whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px] md:max-w-[200px] xl:max-w-[240px] text-left">
              {/* {truncateText(docDetails?.qualification || "")}/ */}
              {docDetails?.specialization}
            </p>
          </div>
        </div>
        <div className="flex w-full justify-end h-full">
          {/* <select
            name="pageSize"
            id="pageSize"
            className="appearance-none w-[50px] h-[50px] text-lg text-center bg-[#8E8E8E33] rounded-full cursor-pointer outline-0"
            defaultValue={5}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
          </select> */}
        </div>
      </div>
      <div ref={scrollContainerRef} className="side_bar h-full w-full overflow-y-auto pb-6 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {addtionalCheckIndata.is_paused && (
          <div className="flex items-center justify-center w-full mx-auto mb-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md border border-red-400/30 animate-fadeIn">
              {/* Warning Icon */}
              <IoWarning className="w-4 h-4 shrink-0" />

              {/* Message */}
              <p className="text-[10px] md:text-xs font-medium leading-snug text-center">
                {addtionalCheckIndata.pause_reason}
              </p>
            </div>
          </div>
        )}
        {roomNames.length === 1 && tvConfig?.room_number && (
          <CompactRoomDisplay roomName={roomNames[0].room_name} />
        )}
        {inClinicData?.length ? (
          <>
            {inClinicData
              ?.filter(
                (item) =>
                  item.status === 2 ||
                  (item.transferred_doctor_mapping_id === mapping_id &&
                    item.transfer_status === 2)
              )
              .sort((a, b) => sortQueueBookings(a, b, sessionDetails?.queue_type))
              .map((item, index) => (
                <ColumnPatient
                  onGoing
                  key={index}
                  pos={item.token_number}
                  status={item.status}
                  name={item.booking_alias || item.full_name}
                  prn={item.prn_number}
                  queue_type={String(sessionDetails?.queue_type)}
                  type={item.booking_mode}
                  hospitalID={hospData?.hospital_id}
                  slotStartTime={item?.booked_slot_start}
                  slotEndTime={item?.booked_slot_end}
                  emergency={item.emergency}
                  showRoomName={displayPatientRoomname}
                  allotedRoom={
                    displayPatientRoomname ? item?.room_name : undefined
                  }
                  onHold={item.onhold}
                  recent_transfer_id={item.recent_transfer_id}
                  transferStatus={item.transfer_status}
                  isTransferred={isTransferredPatient(item)}
                />
              ))}

            {inClinicData?.filter(
                (item) =>
                  !(
                    item.status === 2 ||
                    (item.transferred_doctor_mapping_id === mapping_id &&
                      item.transfer_status === 2)
                  )
              ).length !== 0 ? (
              inClinicData
                ?.filter(
                  (item) =>
                    !(
                      item.status === 2 ||
                      (item.transferred_doctor_mapping_id === mapping_id &&
                        item.transfer_status === 2)
                    )
                )
                .sort((a, b) => sortQueueBookings(a, b, sessionDetails?.queue_type))
                .map((item, index) => (
                  <ColumnPatient
                    key={index}
                    pos={item.token_number}
                    status={item.status}
                    name={item.booking_alias || item.full_name}
                    prn={item.prn_number}
                    queue_type={String(sessionDetails?.queue_type)}
                    type={item.status === 2 ? "Ongoing" : item.booking_mode}
                    hospitalID={hospData?.hospital_id}
                    slotStartTime={item?.booked_slot_start}
                    slotEndTime={item?.booked_slot_end}
                    emergency={item.emergency}
                    showRoomName={displayPatientRoomname}
                    allotedRoom={
                      displayPatientRoomname ? item?.room_name : undefined
                    }
                    onHold={item.onhold}
                    transferStatus={item.transfer_status}
                    recent_transfer_id={item.recent_transfer_id}
                    isTransferred={isTransferredPatient(item)}
                  />
                ))
            ) : (
              inClinicData?.filter(
                (item) =>
                  item.status === 2 ||
                  (item.transferred_doctor_mapping_id === mapping_id &&
                    item.transfer_status === 2)
              ).length === 0 && (
              <ColumnPatient
                empty
                hospitalID={hospData?.hospital_id}
                text={"No Patients in the clinic"}
              />
              )
            )}
          </>
        ) : (
          <ColumnPatient
            empty
            hospitalID={hospData?.hospital_id}
            text={"No Patients in the clinic"}
          />
        )}
      </div>

      {/* <p>{session?.label}</p> */}
      <AudioPermissionModal
        isOpen={showPermissionModal}
        onAllow={handleAllowAudio}
        onCancel={handleCancelAudio}
      />
    </div>
  );
};

export default LiveQueue;
